import React from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt } from 'lucide-react';
import { WasteType } from '../types';
import { localized } from '../utils/localized';
import { useLang } from '../hooks/useLang';
import BottomSheet from './BottomSheet';
import WasteIcon from './WasteIcon';
import styles from './WasteDetailSheet.module.scss';

interface WasteDetailSheetProps {
    /** The waste type to display, or null to hide the sheet */
    wasteType: WasteType | null;
    /** Callback when the sheet is closed */
    onClose: () => void;
}

/**
 * Waste-specific bottom sheet that shows disposal tips.
 * Composes the generic BottomSheet with waste-type content.
 */
const WasteDetailSheet: React.FC<WasteDetailSheetProps> = ({ wasteType, onClose }) => {
    const { t } = useTranslation();
    const lang = useLang();

    const name = localized(wasteType, 'name', lang);

    const icon = wasteType ? (
        <WasteIcon wasteId={wasteType.waste_id} wasteType={wasteType} size={28} />
    ) : undefined;

    return (
        <BottomSheet
            isOpen={!!wasteType}
            onClose={onClose}
            title={name}
            subtitle={t('waste_disposal_tips')}
            icon={icon}
            ariaLabel={t('waste_disposal_tips')}
        >
            {wasteType && (
                <>
                    {/* How to dispose */}
                    {localized(wasteType, 'how', lang) && (
                        <div className={styles.section}>
                            <h3 className={styles.sectionLabel}>{t('how_to_dispose')}</h3>
                            <p className={styles.sectionText}>
                                {localized(wasteType, 'how', lang)}
                            </p>
                        </div>
                    )}

                    {/* What is it */}
                    {localized(wasteType, 'what', lang) && (
                        <div className={styles.section}>
                            <h3 className={styles.sectionLabel}>{t('what_is_it')}</h3>
                            <p className={styles.sectionText}>
                                {localized(wasteType, 'what', lang)}
                            </p>
                        </div>
                    )}

                    {/* Fee notice */}
                    {wasteType.is_fee_based && (
                        <div className={`${styles.section} ${styles.feeNotice}`}>
                            <Receipt size={20} className={styles.feeIcon} aria-hidden="true" />
                            <p>{t('fee_based_description')}</p>
                        </div>
                    )}
                </>
            )}
        </BottomSheet>
    );
};

export default WasteDetailSheet;
